import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Conversation, ConversationStatus } from '../lib/types';

export interface ChatsFilter {
  status?: ConversationStatus;
  /** Server-side status set for the active tabs (open/pending/snoozed), so a
   *  large archive of RESOLVED chats (e.g. a Freshchat import) can't fill the
   *  newest-N window and hide genuinely-active chats. Ignored when `status` is
   *  set (single-status views like Resolved). */
  statusIn?: ConversationStatus[];
  inboxIds?: string[];
  assigneeUid?: string;
  labels?: string[];
  teamId?: string;
  limit?: number;
}

// ── Label search ────────────────────────────────────────────────────────────
// Labels used to be filtered ONLY client-side, over whatever the newest-N
// window happened to hold. A label the AI applied 200 conversations ago
// therefore matched nothing, which is exactly how "search by tag" got reported
// as broken. The filter now rides in the query itself (index:
// chats(labels ARRAY_CONTAINS, lastMessageAt DESC)) so it searches the whole
// collection, with the old client-side pass kept as the documented fallback.
//
// Two hard Firestore limits decide whether the pushdown is legal at all:
//   1. at most ONE array-contains-any per query (we add exactly one), and
//   2. the filters must expand to <= 30 disjunctions in disjunctive normal
//      form — and `status in [...]` / `inboxId in [...]` are disjunctions too,
//      so the real budget is statuses x inboxes x labels.
// Over budget, Firestore rejects the query outright; we keep the client-side
// path instead of shipping something that throws.
const MAX_DISJUNCTIONS = 30;

/** Greppable log events, deliberately the SAME names the server uses (see
 *  src/lib/firestore-errors.ts) so one log-based alert covers both halves. */
const MISSING_INDEX_EVENT = 'firestore.index.missing';
const QUERY_FAILED_EVENT = 'firestore.query.failed';

/**
 * A Firestore query with no composite index does not come back empty — it is
 * REJECTED (failed-precondition). Silently degrading that to "no rows" is how a
 * whole filter reads as broken with nothing in the console, so every swallowed
 * query failure here goes through `logQueryFailure` first.
 */
function isMissingIndexError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const { code, message } = e as { code?: unknown; message?: unknown };
  const text = typeof message === 'string' ? message : '';
  // Web SDK: code is the lower-kebab 'failed-precondition'. Some transports
  // only prefix the message ("9 FAILED_PRECONDITION: The query requires…").
  const failedPrecondition =
    (typeof code === 'string' && code.toLowerCase() === 'failed-precondition') ||
    /FAILED_PRECONDITION/i.test(text);
  if (!failedPrecondition) return false;
  return /requires an index|requires a [A-Z_]+ index|no matching index found|index is currently building/i.test(
    text
  );
}

/** The "create it here" console link Firestore embeds in the error, if present. */
function missingIndexConsoleUrl(e: unknown): string | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const { message } = e as { message?: unknown };
  if (typeof message !== 'string') return undefined;
  const m = /https?:\/\/console\.(?:firebase|cloud)\.google\.com\/\S+/.exec(message);
  return m ? m[0] : undefined;
}

/** Log a swallowed query failure. Returns true when it was a missing index. */
function logQueryFailure(e: unknown, ctx: { site: string; fields: string[]; where: string }): boolean {
  const missing = isMissingIndexError(e);
  const base = {
    collection: 'chats',
    ...ctx,
    error: e instanceof Error ? e.message : String(e),
  };
  if (missing) {
    const url = missingIndexConsoleUrl(e);
    console.error(MISSING_INDEX_EVENT, { ...base, ...(url ? { createIndexUrl: url } : {}) });
  } else {
    console.warn(QUERY_FAILED_EVENT, base);
  }
  return missing;
}

/** How many disjunctions the non-label half of the query already costs. */
function disjunctionsWithoutLabels(filter: ChatsFilter): number {
  const statuses = filter.status ? 1 : filter.statusIn?.length || 1;
  const inboxes = filter.inboxIds && filter.inboxIds.length > 0 ? Math.min(filter.inboxIds.length, 10) : 1;
  return statuses * inboxes;
}

/** True when the label filter can legally ride in the query. */
function labelsPushable(filter: ChatsFilter): boolean {
  const count = filter.labels?.length ?? 0;
  if (count === 0) return false;
  return count * disjunctionsWithoutLabels(filter) <= MAX_DISJUNCTIONS;
}

/**
 * The shared filter half of the query. `pushLabels` adds the array-contains-any
 * clause; the live listener and the paging fetch MUST build it identically or
 * "Load more" would page through a different set than the one on screen.
 */
function buildConstraints(filter: ChatsFilter, pushLabels: boolean): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  if (filter.status) constraints.push(where('status', '==', filter.status));
  else if (filter.statusIn && filter.statusIn.length > 0) constraints.push(where('status', 'in', filter.statusIn));
  if (filter.assigneeUid) constraints.push(where('assigneeUid', '==', filter.assigneeUid));
  if (filter.teamId) constraints.push(where('teamId', '==', filter.teamId));
  if (filter.inboxIds && filter.inboxIds.length > 0) {
    constraints.push(where('inboxId', 'in', filter.inboxIds.slice(0, 10)));
  }
  // array-contains-any is OR across the selected labels. The client-side pass
  // in the snapshot handler still ANDs them, so the semantics agents already
  // have ("show chats carrying ALL of these") are unchanged — the server just
  // narrows the candidate set to the union first, across the whole collection.
  if (pushLabels) constraints.push(where('labels', 'array-contains-any', filter.labels!));
  constraints.push(orderBy('lastMessageAt', 'desc'));
  return constraints;
}

export function useChats(filter: ChatsFilter): {
  chats: Conversation[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** True when a label filter is active but could NOT be pushed into the query
   *  (over the disjunction budget, or the composite index is missing/building),
   *  so matches are limited to the loaded window. The inbox surfaces this — a
   *  short list that is silently short is indistinguishable from no matches. */
  labelSearchDegraded: boolean;
} {
  const [chats, setChats] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(filter.limit ?? 50);
  const [hasMore, setHasMore] = useState(false);
  // Set when the pushdown query is rejected at runtime (missing/building
  // index): re-runs the effect on the old client-side path so the agent gets a
  // working — if narrower — list rather than a dead one.
  const [labelFallback, setLabelFallback] = useState(false);
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const baseLimit = filter.limit ?? 50;

  const labelsKey = filter.labels?.join(',') ?? '';
  const statusInKey = filter.statusIn?.join(',') ?? '';
  const pushLabels = labelsPushable(filter) && !labelFallback;

  // Reset page size when filter changes
  useEffect(() => {
    setPageSize(baseLimit);
    lastDocRef.current = null;
    // A new filter deserves a fresh attempt at the server-side path — the index
    // may have finished building since the last rejection.
    setLabelFallback(false);
  }, [filter.status, statusInKey, filter.assigneeUid, filter.teamId, filter.inboxIds?.join(','), labelsKey, baseLimit]);

  useEffect(() => {
    const constraints = buildConstraints(filter, pushLabels);
    constraints.push(fsLimit(pageSize));

    const q = query(collection(db, 'chats'), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        let docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Conversation, 'id'>) }));
        // Client-side label filter (AND semantics). Still required even when
        // the server narrowed by labels: array-contains-any is a union.
        if (filter.labels && filter.labels.length > 0) {
          docs = docs.filter((c) => filter.labels!.every((l) => (c.labels ?? []).includes(l)));
        }
        setChats(docs);
        lastDocRef.current = snap.docs[snap.docs.length - 1] ?? null;
        setHasMore(snap.docs.length >= pageSize);
        setLoading(false);
      },
      (err) => {
        if (pushLabels) {
          // Audible, then degrade. Never leave the list empty-and-quiet.
          logQueryFailure(err, {
            site: 'useChats:labelSearch',
            fields: ['labels', 'lastMessageAt'],
            where: `labels array-contains-any [${labelsKey}], order lastMessageAt desc`,
          });
          setLabelFallback(true);
          return;
        }
        console.error('useChats listener error', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [filter.status, statusInKey, filter.assigneeUid, filter.teamId, filter.inboxIds?.join(','), labelsKey, pageSize, pushLabels]);

  const loadMore = useCallback(async () => {
    const cursor = lastDocRef.current;
    if (!cursor) {
      // Just grow the listener window
      setPageSize((s) => s + baseLimit);
      return;
    }
    const constraints = buildConstraints(filter, pushLabels);
    constraints.push(startAfter(cursor));
    constraints.push(fsLimit(baseLimit));
    try {
      const q = query(collection(db, 'chats'), ...constraints);
      const snap = await getDocs(q);
      // Bump the page size so the live listener now covers what we just paged in.
      setPageSize((s) => s + snap.docs.length);
      if (snap.docs.length > 0) {
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      setHasMore(snap.docs.length >= baseLimit);
    } catch (err) {
      if (pushLabels) {
        logQueryFailure(err, {
          site: 'useChats:loadMore:labelSearch',
          fields: ['labels', 'lastMessageAt'],
          where: `labels array-contains-any [${labelsKey}], order lastMessageAt desc`,
        });
        // The listener will re-subscribe without the pushdown and reseed the
        // cursor, so don't also page on the old plan.
        setLabelFallback(true);
        return;
      }
      console.error('useChats loadMore error', err);
    }
  }, [filter.status, statusInKey, filter.assigneeUid, filter.teamId, filter.inboxIds?.join(','), labelsKey, baseLimit, pushLabels]);

  return {
    chats,
    loading,
    hasMore,
    loadMore,
    labelSearchDegraded: (filter.labels?.length ?? 0) > 0 && !pushLabels,
  };
}
